import { useMemo } from "react";
import { useAppSelector } from "../../store";
import { getIncentiveViewID } from "../util/IncentivesViewID/IncentiveViewID";

interface IncentiveViewConfig {
  awardCode: number;
  countries: string[];
  startPeriod: number;
  viewId: string;
}

/**
 * Hook to get the incentive view configuration for a specific award code.
 * Checks if the current market and period match the config criteria.
 *
 * @param awardCode - The award code to look up (e.g., 538 for SSI)
 * @returns The matching config or null if no match found
 */
export const useIncentiveViewConfig = (awardCode: number): IncentiveViewConfig | null => {
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { user, configuration } = useAppSelector((state) => state.boot);

  return useMemo(() => {
    const incentiveViews = configuration.incentiveViews || [];

    const viewId = getIncentiveViewID(awardCode, selectedPeriod, configuration, user.isoCountryCode);
    if (!viewId) return null;

    const matchingConfig = incentiveViews.find(
      (config: IncentiveViewConfig) =>
        config.awardCode === awardCode &&
        config.countries.includes(user.isoCountryCode) &&
        parseInt(selectedPeriod, 10) >= config.startPeriod,
    );

    return matchingConfig || null;
  }, [configuration, selectedPeriod, user.isoCountryCode, awardCode]);
};
